import type { LDContext } from 'launchdarkly-react-client-sdk';
import { isUndefined, omitBy } from 'lodash-es';

import type { ActiveStudySite, AuthUser } from 'common/auth/types';

const PLATFORM = 'web';

/** Used before login and after logout; LaunchDarkly generates and persists the key itself. */
export const buildAnonymousLDContext = (): LDContext => ({
    kind: 'user',
    anonymous: true,
    platform: PLATFORM,
});

/**
 * Non-personal attributes only — this data leaves the app for a third party. Names, email, tokens,
 * session id, passcode/security fields, caregivers, dates, and subject-level identifiers
 * (screeningIdentifier, studyInstanceId, armId) stay behind; launchDarklyContext.test.ts enforces it.
 * currentPolicy is excluded for the same reason — it carries updatedByName.firstName/lastName.
 */
export const buildUserLDContext = (activeUser: AuthUser, activeStudySite?: ActiveStudySite): LDContext => {
    const attributes = omitBy(
        {
            platform: PLATFORM,
            userType: activeUser.userType,
            subType: activeUser.subType,
            type: activeUser.type,
            authenticationMethod: activeUser.authenticationMethod,
            baseRole: activeUser.roleBClaim?.sk,
            roleVariant: activeUser.roleVClaim?.sk,
            role: activeUser.role,
            status: activeUser.status,
            preferredLanguage: activeUser.preferredLanguage,
            preferredTimezone: activeUser.preferredTimezone,
            preferredClockFormat: activeUser.preferredClockFormat,
            acceptedVersionPP: activeUser.acceptedVersionPP,
            tn: activeUser.tn,
            parentId: activeUser.parentId,
            subscriberPk: activeUser.subscriberPk,
            sponsorPk: activeUser.sponsorPk,
            customerPk: activeUser.customerPk,
            // Patients make no study/site selection, so fall back to the user record.
            studyId: activeStudySite?.activeStudy?.studyId ?? activeUser.studyId,
            siteId: activeStudySite?.activeSite?.siteId,
        },
        isUndefined
    );

    return {
        kind: 'user',
        key: activeUser.pk,
        anonymous: false,
        ...attributes,
    };
};
